import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import Projects from "../Projects";
import OrdersOverview from "../OrdersOverview";
import { useAuth } from "contexts/AuthContext";

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    members: { total: 0, change: 0 },
    attendance: { today: 0, change: 0 },
    offerings: { total: 0, change: 0 },
    pastors: { total: 0, active: 0 },
  });

  const [charts, setCharts] = useState({
    weeklyAttendance: { labels: [], datasets: { label: "Attendance", data: [] } },
    monthlyOfferings: { labels: [], datasets: { label: "Offerings", data: [] } },
    monthlyTithes: { labels: [], datasets: { label: "Tithes", data: [] } },
  });

  const { church } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const headers = {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        const [
          membersRes,
          attendanceRes,
          offeringsRes,
          pastorsRes,
          weeklyRes,
          monthlyOffRes,
          monthlyTithesRes,
        ] = await Promise.all([
          fetch(`https://api.ofi-cia.com/api/analytics/members-count?church_id=${church.id}`, {
            headers,
          }),
          fetch(`https://api.ofi-cia.com/api/analytics/attendance-count?church_id=${church.id}`, {
            headers,
          }),
          fetch(`https://api.ofi-cia.com/api/analytics/offering-count?church_id=${church.id}`, {
            headers,
          }),
          fetch(`https://api.ofi-cia.com/api/analytics/pastors-count?church_id=${church.id}`, {
            headers,
          }),
          fetch(`https://api.ofi-cia.com/api/analytics/weekly-attendance?church_id=${church.id}`, {
            headers,
          }),
          fetch(`https://api.ofi-cia.com/api/analytics/monthly-offerings?church_id=${church.id}`, {
            headers,
          }),
          fetch(`https://api.ofi-cia.com/api/analytics/monthly-tithes?church_id=${church.id}`, {
            headers,
          }),
        ]);

        const [
          membersData,
          attendanceData,
          offeringsData,
          pastorsData,
          weeklyRaw,
          monthlyOffRaw,
          monthlyTithesRaw,
        ] = await Promise.all([
          membersRes.json(),
          attendanceRes.json(),
          offeringsRes.json(),
          pastorsRes.json(),
          weeklyRes.json(),
          monthlyOffRes.json(),
          monthlyTithesRes.json(),
        ]);

        const isArray = (val) => (Array.isArray(val) ? val : []);

        const weekly = isArray(weeklyRaw.data || weeklyRaw);
        const monthlyOff = isArray(monthlyOffRaw.data || monthlyOffRaw);
        const monthlyTithes = isArray(monthlyTithesRaw.data || monthlyTithesRaw);

        const allDays = [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ];

        const weeklyMap = Object.fromEntries(weekly.map((item) => [item.day, item.count]));
        const weeklyAttendance = {
          labels: allDays,
          datasets: {
            label: "Attendance",
            data: allDays.map((day) => weeklyMap[day] || 0),
          },
        };

        const allMonths = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];

        const buildMonthlyData = (rawData, label) => {
          const map = Object.fromEntries(rawData.map((item) => [item.month, item.amount]));
          return {
            labels: allMonths,
            datasets: {
              label,
              data: allMonths.map((month) => map[month] || 0),
            },
          };
        };

        const monthlyOfferings = buildMonthlyData(monthlyOff, "Offerings");
        const monthlyTithesData = buildMonthlyData(monthlyTithes, "Tithes");

        setStats({
          members: {
            total: membersData.total || 0,
            change: membersData.percentage_change || 0,
          },
          attendance: {
            today: attendanceData.today || 0,
            change: attendanceData.percentage_change || 0,
          },
          offerings: {
            total: offeringsData.this_week || 0,
            change: offeringsData.percentage_change || 0,
          },
          pastors: {
            total: pastorsData.count || 0,
            active: pastorsData.active || 0,
          },
        });

        setCharts({
          weeklyAttendance,
          monthlyOfferings,
          monthlyTithes: monthlyTithesData,
        });
      } catch (error) {
        console.error("Failed to fetch analytics:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <MDBox py={3}>
      <Grid container spacing={3}>
        {/* Cards */}
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="dark"
              icon="people"
              title="Members"
              count={stats.members.total}
              percentage={{
                color: stats.members.change >= 0 ? "success" : "error",
                amount: `${stats.members.change}%`,
                label: "than last week",
              }}
            />
          </MDBox>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              icon="leaderboard"
              title="Today's Service"
              count={stats.attendance.today}
              percentage={{
                color: stats.attendance.change >= 0 ? "success" : "error",
                amount: `${stats.attendance.change}%`,
                label: "than last week",
              }}
            />
          </MDBox>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="success"
              icon="store"
              title="Offerings"
              count={`₵${stats.offerings.total.toLocaleString()}`}
              percentage={{
                color: stats.offerings.change >= 0 ? "success" : "error",
                amount: `${stats.offerings.change}%`,
                label: "than last week",
              }}
            />
          </MDBox>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="primary"
              icon="person_add"
              title="Pastors"
              count={stats.pastors.total}
              percentage={{
                color: "success",
                amount: `${Math.round((stats.pastors.active / (stats.pastors.total || 1)) * 100)}%`,
                label: "Active",
              }}
            />
          </MDBox>
        </Grid>
      </Grid>

      {/* Charts */}
      <MDBox mt={4.5}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={3}>
              <ReportsBarChart
                color="info"
                title="Weekly Attendance"
                description="Number of members present each day this week"
                date="updated just now"
                chart={charts.weeklyAttendance}
              />
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={3}>
              <ReportsLineChart
                color="success"
                title="Monthly Offerings"
                description={`(+${stats.offerings.change}% vs last month)`}
                date="updated just now"
                chart={charts.monthlyOfferings}
              />
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={3}>
              <ReportsLineChart
                color="dark"
                title="Monthly Tithes"
                description="Showing monthly tithe income"
                date="updated just now"
                chart={charts.monthlyTithes}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      {/* Lower Section */}
      <MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={8}>
            {/* <Projects /> */}
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            {/* <OrdersOverview /> */}
          </Grid>
        </Grid>
      </MDBox>
    </MDBox>
  );
}
